import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { API } from '@/lib/api';
import { toast } from 'sonner';
import { TrendingUp, Target, Award, Zap, LogOut, Crown } from 'lucide-react';
import confetti from 'canvas-confetti';
import Navigation from '@/components/Navigation';
import QuickStartGuide from '@/components/QuickStartGuide';

function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [wins, setWins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    loadData();
    
    const hasCompletedGuide = localStorage.getItem('quick_start_completed');
    if (!hasCompletedGuide) {
      setTimeout(() => setShowGuide(true), 1000);
    }
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, winsRes] = await Promise.all([
        API.get('/dashboard/stats'),
        API.get('/wins')
      ]);
      setStats(statsRes.data);
      setWins(winsRes.data.slice(0, 5));
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load dashboard');
      setLoading(false);
    }
  };

  const handleUpgrade = async () => {
    try {
      const response = await API.post('/payments/checkout', {
        plan: 'monthly',
        origin_url: window.location.origin
      });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to start checkout');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navigation />
      
      {showGuide && <QuickStartGuide onClose={() => setShowGuide(false)} />}
      
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-['Outfit'] text-4xl font-bold mb-2" data-testid="dashboard-title">Command Center</h1>
            <p className="text-gray-400">Track your negotiation mastery</p>
          </div>
          {stats?.subscription_status === 'free' && (
            <Button 
              onClick={handleUpgrade}
              className="bg-yellow-500 hover:bg-yellow-400 text-black font-bold"
              data-testid="upgrade-button"
            >
              <Crown className="w-5 h-5 mr-2" /> Upgrade to Premium
            </Button>
          )}
        </div>

        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="ninja-card" data-testid="stat-wins">
            <div className="flex items-center justify-between mb-2">
              <Award className="text-green-500 w-8 h-8" />
              <span className="text-3xl font-bold">{stats?.total_wins || 0}</span>
            </div>
            <p className="text-gray-400 text-sm">Total Wins</p>
          </div>
          <div className="ninja-card" data-testid="stat-saved">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="text-green-500 w-8 h-8" />
              <span className="text-3xl font-bold">£{stats?.total_saved?.toFixed(0) || 0}</span>
            </div>
            <p className="text-gray-400 text-sm">Total Saved</p>
          </div>
          <div className="ninja-card" data-testid="stat-score">
            <div className="flex items-center justify-between mb-2">
              <Target className="text-green-500 w-8 h-8" />
              <span className="text-3xl font-bold">{stats?.avg_score || 0}</span>
            </div>
            <p className="text-gray-400 text-sm">Avg Score</p>
          </div>
          <div className="ninja-card" data-testid="stat-streak">
            <div className="flex items-center justify-between mb-2">
              <Zap className="text-yellow-500 w-8 h-8" />
              <span className="text-3xl font-bold">{stats?.current_streak || 0}</span>
            </div>
            <p className="text-gray-400 text-sm">Day Streak</p>
          </div>
        </div>

        {stats?.subscription_status === 'free' && (
          <div className="ninja-card mb-8 bg-gradient-to-r from-yellow-500/10 to-transparent border-yellow-500/30" data-testid="roleplay-limit-card">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="font-['Outfit'] text-xl font-bold mb-2">Roleplay Sessions</h3>
                <p className="text-gray-400">{stats.roleplay_count_this_month} / 3 used this month</p>
                <div className="progress-bar mt-3 w-48">
                  <div className="progress-indicator" style={{ width: `${(stats.roleplay_count_this_month / 3) * 100}%` }}></div>
                </div>
              </div>
              <Button 
                onClick={handleUpgrade}
                className="bg-yellow-500 hover:bg-yellow-400 text-black font-bold"
                data-testid="upgrade-for-unlimited"
              >
                <Crown className="w-5 h-5 mr-2" /> Upgrade for Unlimited
              </Button>
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          <div className="ninja-card">
            <h3 className="font-['Outfit'] text-2xl font-bold mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <Button 
                onClick={() => navigate('/roleplay')} 
                className="w-full bg-green-600 hover:bg-green-500 justify-start"
                data-testid="start-roleplay-button"
              >
                <Target className="w-5 h-5 mr-3" /> Start AI Roleplay
              </Button>
              <Button 
                onClick={() => navigate('/scripts')} 
                variant="outline"
                className="w-full border-white/20 hover:bg-white/10 justify-start"
                data-testid="generate-script-button"
              >
                <TrendingUp className="w-5 h-5 mr-3" /> Generate Script
              </Button>
              <Button 
                onClick={() => navigate('/challenges')} 
                variant="outline"
                className="w-full border-white/20 hover:bg-white/10 justify-start"
                data-testid="view-challenges-button"
              >
                <Award className="w-5 h-5 mr-3" /> View Challenges
              </Button>
            </div>
          </div>

          <div className="ninja-card">
            <h3 className="font-['Outfit'] text-2xl font-bold mb-4">Recent Wins</h3>
            {wins.length === 0 ? (
              <p className="text-gray-400 text-center py-8">No wins yet. Start negotiating!</p>
            ) : (
              <div className="space-y-3">
                {wins.map(win => (
                  <div key={win.win_id} className="p-3 rounded-lg bg-white/5 border border-white/10">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold">{win.title}</p>
                        <p className="text-sm text-gray-400">{win.category}</p>
                      </div>
                      <span className="text-green-500 font-bold">£{win.amount_saved}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;